import React, { useMemo, useState } from "react";

/**
 * Lightweight dependency-free SVG sparkline for trend data.
 *
 * Props:
 *   data       : [{ x: 'label', y: number|null }, ...]  (null = no data; renders as a gap)
 *   width/height: viewport dims
 *   stroke     : CSS color
 *   fill       : gradient under the line
 *   referenceY : if set, draws a dashed baseline (e.g. 70 for a 70% win-rate target)
 *   yFormat    : fn(value) => display string
 *   positive   : when true, colors the area emerald; when false, auto-splits by sign
 */
export default function Sparkline({
  data = [],
  width = 560,
  height = 140,
  stroke = "#10B981",
  fill = "#10B98133",
  referenceY = null,
  referenceColor = "rgba(148,163,184,0.4)",
  referenceLabel = null,
  yFormat = (v) => String(v),
  padding = { top: 14, right: 10, bottom: 20, left: 36 },
  testid,
}) {
  const [hover, setHover] = useState(null);

  const nonNull = data.filter((d) => d.y != null && !Number.isNaN(d.y));
  const yVals = nonNull.map((d) => d.y);
  if (referenceY != null) yVals.push(referenceY);

  const yMin = yVals.length ? Math.min(...yVals) : 0;
  const yMax = yVals.length ? Math.max(...yVals) : 1;
  const yRange = yMax - yMin || 1;
  const padY = yRange * 0.15;
  const Y0 = yMin - padY;
  const Y1 = yMax + padY;

  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const xScale = (i) => padding.left + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yScale = (v) => padding.top + innerH - ((v - Y0) / (Y1 - Y0)) * innerH;

  // Build line path with gaps for null values
  const pathSegments = useMemo(() => {
    const segs = [];
    let cur = [];
    data.forEach((d, i) => {
      if (d.y == null || Number.isNaN(d.y)) {
        if (cur.length) { segs.push(cur); cur = []; }
      } else {
        cur.push([xScale(i), yScale(d.y), i]);
      }
    });
    if (cur.length) segs.push(cur);
    return segs;
  }, [data, width, height]);

  const linePath = pathSegments
    .map((seg) => seg.map(([x, y], j) => `${j === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" "))
    .join(" ");

  // Area fill (first/last contiguous segment only — simpler and looks good)
  const areaPath = pathSegments.length
    ? (() => {
        const first = pathSegments[0];
        const last = pathSegments[pathSegments.length - 1];
        const baseY = padding.top + innerH;
        const start = `M${first[0][0].toFixed(1)},${baseY.toFixed(1)} L`;
        const mid = pathSegments.flat().map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L");
        const end = ` L${last[last.length - 1][0].toFixed(1)},${baseY.toFixed(1)} Z`;
        return start + mid + end;
      })()
    : "";

  const gradId = `spark-grad-${testid || Math.random().toString(36).slice(2)}`;

  // Tick labels: show first, middle, last date
  const xTicks = data.length
    ? [{ i: 0, label: data[0].x }, ...(data.length > 10 ? [{ i: Math.floor(data.length / 2), label: data[Math.floor(data.length / 2)].x }] : []), { i: data.length - 1, label: data[data.length - 1].x }]
    : [];

  const yTicks = [Y0, (Y0 + Y1) / 2, Y1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-auto block"
      data-testid={testid}
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        if (!data.length) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const localX = ((e.clientX - rect.left) / rect.width) * width;
        const i = Math.max(0, Math.min(data.length - 1, Math.round(((localX - padding.left) / innerW) * (data.length - 1))));
        setHover({ i, point: data[i] });
      }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Y-axis gridlines */}
      {yTicks.map((v) => (
        <g key={`ytick-${v}`}>
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={yScale(v)}
            y2={yScale(v)}
            stroke="rgba(148,163,184,0.08)"
            strokeDasharray="2 4"
          />
          <text
            x={padding.left - 6}
            y={yScale(v) + 3}
            textAnchor="end"
            fontSize="9"
            fontFamily="ui-monospace, monospace"
            fill="rgba(148,163,184,0.55)"
          >
            {yFormat(v)}
          </text>
        </g>
      ))}

      {/* Reference line (e.g. 70% target) */}
      {referenceY != null && (
        <g>
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={yScale(referenceY)}
            y2={yScale(referenceY)}
            stroke={referenceColor}
            strokeDasharray="4 4"
          />
          {referenceLabel && (
            <text
              x={width - padding.right}
              y={yScale(referenceY) - 4}
              textAnchor="end"
              fontSize="9"
              fontFamily="ui-monospace, monospace"
              fill={referenceColor}
            >
              {referenceLabel}
            </text>
          )}
        </g>
      )}

      {/* Area fill */}
      <path d={areaPath} fill={`url(#${gradId})`} />

      {/* Line */}
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Dot on last visible point */}
      {pathSegments.length > 0 && (() => {
        const last = pathSegments[pathSegments.length - 1];
        const [lx, ly] = last[last.length - 1];
        return <circle cx={lx} cy={ly} r="3.5" fill={stroke} stroke="#0B0C12" strokeWidth="1.5" />;
      })()}

      {/* Hover */}
      {hover && hover.point.y != null && (
        <g>
          <line
            x1={xScale(hover.i)}
            x2={xScale(hover.i)}
            y1={padding.top}
            y2={padding.top + innerH}
            stroke="rgba(16,185,129,0.4)"
            strokeDasharray="2 2"
          />
          <circle cx={xScale(hover.i)} cy={yScale(hover.point.y)} r="4" fill={stroke} stroke="#0B0C12" strokeWidth="1.5" />
          <g transform={`translate(${Math.min(width - 100, Math.max(padding.left, xScale(hover.i) + 8))}, ${padding.top + 4})`}>
            <rect x="0" y="0" width="92" height="34" rx="4" fill="#0B0C12" stroke="rgba(16,185,129,0.3)" />
            <text x="6" y="13" fontSize="9" fontFamily="ui-monospace, monospace" fill="rgba(148,163,184,0.8)">{hover.point.x}</text>
            <text x="6" y="27" fontSize="11" fontFamily="ui-monospace, monospace" fontWeight="700" fill={stroke}>{yFormat(hover.point.y)}</text>
          </g>
        </g>
      )}

      {/* X-axis tick labels */}
      {xTicks.map((t) => (
        <text
          key={t.i}
          x={xScale(t.i)}
          y={height - 4}
          textAnchor={t.i === 0 ? "start" : t.i === data.length - 1 ? "end" : "middle"}
          fontSize="9"
          fontFamily="ui-monospace, monospace"
          fill="rgba(148,163,184,0.55)"
        >
          {t.label}
        </text>
      ))}
    </svg>
  );
}
